import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageHeader } from '../ui/PageHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonRows } from '../ui/Skeleton';
import { supabase } from '../lib/supabase';
import { timeAgo } from '../lib/timeAgo';
import { RefImagePicker, type RefImage } from '../components/RefImagePicker';
import { EventTextsTab } from './EventTextsTab';
import { EventPushTab } from './EventPushTab';
import { EventGraphicsTab } from './EventGraphicsTab';
import { EventPopupTab, type EventPopupConfig } from './EventPopupTab';

/**
 * EVENTS — the admin side of the Shado Partiful alternative (Evan 2026-10-21: "yes just a whole
 * new tab on my admin sidebar" / "i should be able to customize all of this").
 *
 * Tables: shado_events / shado_event_items / shado_event_rsvps (shado-admin/supabase/sql/
 * events_v1.sql — the plain `events` name was already taken by the events-sync ingest table).
 * Admin edits hit the tables directly under the admin RLS policies; the public page reads only
 * through the anon-granted get_public_event / event_rsvp RPCs.
 *
 * Per-event everything: page blocks (lineup/requirements/guest wall/countdown/RSVP) toggle on
 * and off; items are tickets OR merch with free/paid prices, sizes, optional Stripe Payment
 * Link (buttons say "reserve with your RSVP" until one is set), inventory, and an entitlement
 * JSON slot reserved for the Shado-activity discounts that land with real checkout.
 *
 * Images upload to the public studio-assets bucket under events/<slug>/ (admin-scoped storage
 * policies in the same SQL file).
 */

const PUBLIC_HOST = 'https://events.joinshado.com'; // DNS live 2026-10-22 (CNAME → Vercel)

/** Hero-generator state survives a page refresh (Evan 2026-10-28: a refresh "got rid of my
 * generate history and reference images, it should not"). Prompt + ref thumbnails + the URLs of
 * past generations live in localStorage (the generated images themselves are in Supabase storage,
 * so URLs are enough to bring the history back). Reads/writes are wrapped — storage can be
 * unavailable or full, and refs are the first thing dropped when it is. */
const HERO_GEN_LS = 'shado-admin:hero-gen:v1';
type HeroGenSaved = { prompt?: string; refs?: RefImage[]; history?: string[] };
function loadHeroGen(): HeroGenSaved {
  try {
    return JSON.parse(localStorage.getItem(HERO_GEN_LS) || '{}') as HeroGenSaved;
  } catch {
    return {};
  }
}
function saveHeroGen(next: HeroGenSaved) {
  try {
    localStorage.setItem(HERO_GEN_LS, JSON.stringify(next));
  } catch {
    try {
      localStorage.setItem(HERO_GEN_LS, JSON.stringify({ prompt: next.prompt, history: next.history }));
    } catch {
      /* storage unavailable */
    }
  }
}

type EventRow = {
  id: string;
  slug: string;
  title: string;
  tagline: string | null;
  flyer_url: string | null;
  starts_at: string | null;
  doors_at: string | null;
  /** Custom hero date text (events_v14) — blank = the page derives it from starts_at. */
  date_line?: string | null;
  ends_at: string | null;
  venue_name: string | null;
  /** events_v23: RSVP hard cap; null = unlimited. event_rsvp refuses new guests at the cap. */
  max_capacity?: number | null;
  address: string | null;
  city: string | null;
  show_lineup: boolean;
  show_requirements: boolean;
  show_guest_list: boolean;
  show_countdown: boolean;
  rsvp_open: boolean;
  published: boolean;
  /** Custom timeline rows on the public when-card; empty → page derives Doors/Starts/Ends. */
  schedule?: { time?: string | null; label: string }[];
  /** Bullets shown on the RIGHT side of the when-card (events_v11). */
  schedule_right?: string[];
  lineup: { act: string; time?: string }[];
  requirements: string[];
  /** Per-event visual: accent recolors the page, hero picks the flyer treatment; the two
   * *_color keys recolor the hero eyebrow + title. */
  theme: {
    accent?: string | null;
    hero?: 'takeover' | 'framed';
    presents_color?: string | null;
    title_color?: string | null;
    /** Link-preview text line (iMessage/Snapchat/Insta card). Empty → "RSVP to <title>". */
    og_title?: string | null;
  } | null;
  /** Email / phone / URL → the public page's bottom Contact button (events_v4_contact.sql). */
  contact?: string | null;
  /** General description + bullets rendered above the when/where card (events_v10). */
  description?: string | null;
  description_bullets?: string[];
  /**
   * In-app popup config (20261102000000). Copy + targeting only — the rating requirement lives on
   * the free ticket item's `entitlement.min_logs` and is read, never duplicated, by the popup.
   */
  popup?: EventPopupConfig | null;
  created_at: string;
};

type ItemRow = {
  id: string;
  event_id: string;
  kind: 'ticket' | 'merch';
  name: string;
  description: string | null;
  image_url: string | null;
  price_cents: number;
  sizes: string[];
  stripe_payment_link: string | null;
  /** Custom button copy on the public page (default: Reserve with Shado account / Purchase / Get it). */
  cta_label?: string | null;
  /** Grey, non-bold second line under the (purple, bold) description on the page. */
  subdescription?: string | null;
  inventory: number | null;
  sold: number;
  sort: number;
  active: boolean;
  /** Shado perk: {min_logs, discount_pct, label} — enforced server-side (checkout discount on
   * paid items; reserve requirement on free ones). label = Evan's custom pill/refusal copy. */
  entitlement: { min_logs?: number; discount_pct?: number; label?: string } | null;
  /** Ticket bundles: merch item ids this item includes. */
  bundle_item_ids: string[] | null;
  /** Merch only (events_v21): true = Stripe collects a US address; false = checkout says
   * "Pick up your order at the event." */
  collect_shipping?: boolean;
};

type OrderRow = {
  id: string;
  item_id: string | null;
  stripe_session_id: string;
  status: string;
  amount_cents: number;
  qty: number;
  size: string | null;
  buyer_name: string | null;
  buyer_email: string | null;
  buyer_phone: string | null;
  /** Stripe shipping_address_collection result for merch (events_v19). */
  shipping?: { name?: string | null; address?: Record<string, string | null> } | null;
  /** Manual fulfillment mark (events_v20) — Evan ships merch himself. */
  shipped_at?: string | null;
  created_at: string;
};

/** "123 Main St, Austin TX 78701" from Stripe's shipping_details — one line for the order row. */
function shippingLine(s: OrderRow['shipping']): string | null {
  const a = s?.address;
  if (!a) return null;
  const parts = [a.line1, a.line2, a.city, [a.state, a.postal_code].filter(Boolean).join(' ')]
    .map((p) => (p ?? '').trim())
    .filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

type RsvpRow = {
  id: string;
  name: string;
  phone: string | null;
  user_id: string | null;
  created_at: string;
  username?: string | null;
  /** Per-guest stats from admin_event_rsvp_stats (events_v17); null for guests w/o account. */
  friends?: number | null;
  ratings?: number | null;
  joined?: string | null;
  /** Door check-in mark (events_v22). */
  checked_in_at?: string | null;
  /** Pending claimer (events_v24): verified but short of the item's min_logs. */
  pending?: boolean;
  needed?: number | null;
  /** 21 or older on the event date (events_v27) — null when no birthdate on the profile. */
  is_21?: boolean | null;
};

/** Swap item i with its neighbor (dir −1/+1) — the editor's reorder arrows. */
function moveItem<T>(arr: T[], i: number, dir: -1 | 1): T[] {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const copy = arr.slice();
  [copy[i], copy[j]] = [copy[j], copy[i]];
  return copy;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

function dollarsToCents(v: string): number {
  const n = Number(v.replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}
function centsToDollars(c: number): string {
  return c % 100 === 0 ? String(c / 100) : (c / 100).toFixed(2);
}

/** Formats every browser can RENDER. Anything else (HEIC above all — a Mac file picker hands
 * those over raw, and the stored image is then broken everywhere but iPhones; bit Evan on
 * 2026-10-27) gets re-encoded to JPEG in the browser before upload. */
const WEB_SAFE_IMAGE = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);

async function toWebSafeImage(file: File): Promise<{ blob: Blob; ext: string; mime: string }> {
  if (WEB_SAFE_IMAGE.has(file.type)) {
    const ext = file.type === 'image/jpeg' ? 'jpg' : file.type.slice(6);
    return { blob: file, ext, mime: file.type };
  }
  // Safari decodes HEIC natively; draw it to a canvas and export JPEG.
  const url = URL.createObjectURL(file);
  try {
    const img: HTMLImageElement = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () =>
        reject(
          new Error(
            `This browser can’t read ${file.name.split('.').pop()?.toUpperCase() || 'that'} files — export the photo as JPG or PNG (or use Safari) and try again.`,
          ),
        );
      i.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.getContext('2d')!.drawImage(img, 0, 0);
    const blob: Blob = await new Promise((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Image conversion failed'))), 'image/jpeg', 0.9),
    );
    return { blob, ext: 'jpg', mime: 'image/jpeg' };
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function uploadEventImage(slug: string, file: File): Promise<string> {
  const safe = await toWebSafeImage(file);
  const path = `events/${slug}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${safe.ext}`;
  const { error } = await supabase.storage
    .from('studio-assets')
    .upload(path, safe.blob, { contentType: safe.mime, upsert: false });
  if (error) throw new Error(error.message);
  return supabase.storage.from('studio-assets').getPublicUrl(path).data.publicUrl;
}

const inputCls =
  'w-full rounded-lg border border-border bg-surface px-3 py-2 text-[13px] text-white placeholder:text-mutedDim focus:border-accent focus:outline-none';
const labelCls = 'mb-1 block text-[10.5px] uppercase tracking-[0.08em] text-mutedDim';

function Toggle({
  on,
  label,
  onChange,
}: {
  on: boolean;
  label: string;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      className={`rounded-full border px-3 py-1 text-[11.5px] transition-colors ${
        on
          ? 'border-accent/50 bg-accent/15 text-accent'
          : 'border-border bg-surface text-mutedDim'
      }`}
    >
      {label}
    </button>
  );
}

/* Tap-to-pick color swatches — Evan's ask (2026-10-27): no native RGB picker. */
const SWATCHES: { hex: string; name: string }[] = [
  { hex: '#8B5CF6', name: 'Shado violet' },
  { hex: '#C4AEFF', name: 'Light violet' },
  { hex: '#FFFFFF', name: 'White' },
  { hex: '#F5F0E8', name: 'Cream' },
  { hex: '#A3A3A3', name: 'Grey' },
  { hex: '#F472B6', name: 'Pink' },
  { hex: '#EF4444', name: 'Red' },
  { hex: '#F97316', name: 'Orange' },
  { hex: '#FBBF24', name: 'Gold' },
  { hex: '#34D399', name: 'Green' },
  { hex: '#60A5FA', name: 'Blue' },
];

function Swatches({
  value,
  fallback,
  onPick,
}: {
  value: string | null | undefined;
  fallback: string;
  onPick: (hex: string) => void;
}) {
  const current = (value ?? fallback).toUpperCase();
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {SWATCHES.map((s) => (
        <button
          key={s.hex}
          type="button"
          title={s.name}
          aria-label={s.name}
          onClick={() => onPick(s.hex)}
          className={`h-6 w-6 rounded-full border transition-transform ${
            current === s.hex
              ? 'scale-110 border-white ring-2 ring-accent/60'
              : 'border-white/15 hover:scale-105'
          }`}
          style={{ background: s.hex }}
        />
      ))}
    </span>
  );
}

function ItemEditor({
  item,
  slug,
  siblings,
  onSaved,
  onDeleted,
}: {
  item: ItemRow;
  slug: string;
  siblings: ItemRow[];
  onSaved: (row: ItemRow) => void;
  onDeleted: (id: string) => void;
}) {
  const [draft, setDraft] = useState(item);
  const [priceText, setPriceText] = useState(centsToDollars(item.price_cents));
  const [sizesText, setSizesText] = useState(item.sizes.join(', '));
  const [perkLogsText, setPerkLogsText] = useState(
    item.entitlement?.min_logs ? String(item.entitlement.min_logs) : '',
  );
  const [perkPctText, setPerkPctText] = useState(
    item.entitlement?.discount_pct ? String(item.entitlement.discount_pct) : '',
  );
  const [perkLabelText, setPerkLabelText] = useState(item.entitlement?.label ?? '');
  const [bundleIds, setBundleIds] = useState<string[]>(item.bundle_item_ids ?? []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  /** Drag-and-drop onto the image row = same as picking the file (Evan 2026-10-28). */
  const [imgDragOver, setImgDragOver] = useState(false);
  const uploadItemImage = useCallback(
    async (f: File) => {
      try {
        const url = await uploadEventImage(slug, f);
        setDraft((d) => ({ ...d, image_url: url }));
      } catch (ex) {
        setErr(ex instanceof Error ? ex.message : 'upload failed');
      }
    },
    [slug],
  );
  const bundleCandidates = siblings.filter((s) => s.id !== item.id && s.kind === 'merch');

  const save = async (extra?: Partial<ItemRow>) => {
    setBusy(true);
    setErr(null);
    const minLogs = Math.trunc(Number(perkLogsText) || 0);
    const pct = Math.min(Math.max(Math.trunc(Number(perkPctText) || 0), 0), 100);
    const next: ItemRow = {
      ...draft,
      ...extra,
      price_cents: dollarsToCents(priceText),
      sizes: sizesText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      // min logs alone is enough now: on a FREE item it is the reserve requirement (no % off
      // needed); the optional label overrides the page's pill AND the refusal message.
      entitlement:
        minLogs > 0
          ? {
              min_logs: minLogs,
              ...(pct > 0 ? { discount_pct: pct } : {}),
              ...(perkLabelText.trim() ? { label: perkLabelText.trim() } : {}),
            }
          : null,
      bundle_item_ids: bundleIds.length > 0 ? bundleIds : null,
    };
    const { error } = await supabase
      .from('shado_event_items')
      .update({
        kind: next.kind,
        name: next.name,
        description: next.description,
        subdescription: next.subdescription?.trim() || null,
        // A .heic URL is a stale draft from before the uploader converted — saving it back
        // re-breaks the image on every non-iPhone (bit Evan twice on 2026-10-27). Never write one.
        image_url: next.image_url?.toLowerCase().endsWith('.heic') ? undefined : next.image_url,
        price_cents: next.price_cents,
        sizes: next.sizes,
        stripe_payment_link: next.stripe_payment_link || null,
        ...('cta_label' in next || 'cta_label' in item
          ? { cta_label: next.cta_label?.trim() || null }
          : {}),
        inventory: next.inventory,
        sort: next.sort,
        active: next.active,
        entitlement: next.entitlement,
        bundle_item_ids: next.bundle_item_ids,
      })
      .eq('id', item.id);
    setBusy(false);
    if (error) {
      setErr(error.message);
      return;
    }
    setDraft(next);
    onSaved(next);
  };

  return (
    <div className="admin-card space-y-2 !p-3">
      <div className="flex flex-wrap items-center gap-2">
        <select
          className={`${inputCls} !w-auto`}
          value={draft.kind}
          onChange={(e) => setDraft({ ...draft, kind: e.target.value as ItemRow['kind'] })}
        >
          <option value="ticket">Ticket</option>
          <option value="merch">Merch</option>
        </select>
        <textarea
          className={`${inputCls} !h-auto !w-48 flex-1 resize-y !py-2`}
          rows={1}
          value={draft.name}
          placeholder="Name (Enter = line break on the page)"
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        />
        <div className="flex items-center gap-1">
          <span className="text-[12px] text-mutedDim">$</span>
          <input
            className={`${inputCls} !w-20`}
            value={priceText}
            placeholder="0 = free"
            onChange={(e) => setPriceText(e.target.value)}
          />
        </div>
        <Toggle on={draft.active} label={draft.active ? 'active' : 'hidden'} onChange={(v) => setDraft({ ...draft, active: v })} />
      </div>
      <div className="flex items-start gap-2">
        {/* Color toggle for the description on the page — purple (default) or grey, stored as a
            leading ~ on the text. */}
        <button
          type="button"
          title={
            (draft.description ?? '').startsWith('~')
              ? 'Grey on the page — click for purple'
              : 'Purple on the page — click for grey'
          }
          className={`mt-2.5 h-4 w-4 flex-none rounded-full border ${
            (draft.description ?? '').startsWith('~') ? 'border-border bg-zinc-500' : 'border-accent bg-accent'
          }`}
          onClick={() =>
            setDraft({
              ...draft,
              description: (draft.description ?? '').startsWith('~')
                ? (draft.description ?? '').slice(1) || null
                : `~${draft.description ?? ''}`,
            })
          }
        />
        <textarea
          className={`${inputCls} min-h-[44px] flex-1`}
          value={(draft.description ?? '').startsWith('~') ? (draft.description ?? '').slice(1) : draft.description ?? ''}
          placeholder="Description (what's included, bundle notes…)"
          onChange={(e) =>
            setDraft({
              ...draft,
              description: e.target.value
                ? (draft.description ?? '').startsWith('~')
                  ? `~${e.target.value}`
                  : e.target.value
                : null,
            })
          }
        />
      </div>
      <input
        className={inputCls}
        value={draft.subdescription ?? ''}
        placeholder="Sub-description (grey line under it — fine print, details)"
        onChange={(e) => setDraft({ ...draft, subdescription: e.target.value || null })}
      />
      <div className="flex flex-wrap items-center gap-2">
        {draft.kind === 'merch' ? (
          /* Checkbox pills instead of free text (Evan 2026-10-28: "I should have to check boxes
             for each size not just input text"). Checked sizes become the Stripe Checkout size
             dropdown; sizesText stays the save format underneath. */
          <div className="flex items-center gap-1">
            <span className="mr-1 text-[10px] uppercase tracking-[0.1em] text-mutedDim">Sizes</span>
            {['XS', 'S', 'M', 'L', 'XL', 'XXL'].map((s) => {
              const cur = sizesText
                .split(',')
                .map((x) => x.trim().toUpperCase())
                .filter(Boolean);
              const on = cur.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    const set = new Set(cur);
                    if (on) set.delete(s);
                    else set.add(s);
                    const order = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
                    setSizesText(
                      order
                        .filter((z) => set.has(z))
                        .concat(Array.from(set).filter((z) => !order.includes(z)))
                        .join(', '),
                    );
                  }}
                  className={`rounded-md border px-2 py-0.5 text-[11px] ${
                    on
                      ? 'border-accent bg-accent/15 text-accent'
                      : 'border-border text-mutedDim hover:text-white'
                  }`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        ) : null}
        <input
          className={`${inputCls} !w-24`}
          value={draft.inventory ?? ''}
          placeholder="Qty (∞)"
          onChange={(e) =>
            setDraft({ ...draft, inventory: e.target.value ? Number(e.target.value) || null : null })
          }
        />
        <input
          className={`${inputCls} flex-1 !w-52`}
          value={draft.stripe_payment_link ?? ''}
          placeholder="Stripe Payment Link (optional override — checkout is automatic)"
          onChange={(e) => setDraft({ ...draft, stripe_payment_link: e.target.value || null })}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-[0.06em] text-mutedDim">Button text</span>
        <input
          className={`${inputCls} !w-64 flex-1`}
          value={draft.cta_label ?? ''}
          placeholder={
            draft.kind === 'ticket'
              ? draft.price_cents > 0
                ? 'Purchase (default)'
                : 'Reserve with Shado account (default)'
              : 'Get it (default)'
          }
          onChange={(e) => setDraft({ ...draft, cta_label: e.target.value || null })}
        />
        <span className="text-[10.5px] text-mutedDim">the purple button on the public page</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-[0.06em] text-mutedDim">Shado perk</span>
        <input
          className={`${inputCls} !w-24`}
          value={perkLogsText}
          placeholder="min logs"
          onChange={(e) => setPerkLogsText(e.target.value)}
        />
        <input
          className={`${inputCls} !w-20`}
          value={perkPctText}
          placeholder="% off"
          onChange={(e) => setPerkPctText(e.target.value)}
        />
        <input
          className={`${inputCls} !w-64 flex-1`}
          value={perkLabelText}
          placeholder="custom message (optional — shown on the pill + as the refusal)"
          onChange={(e) => setPerkLabelText(e.target.value)}
        />
        <span className="text-[10.5px] text-mutedDim">
          paid item: that many logged nights = the discount at checkout (100 = free). FREE item:
          min logs is the REQUIREMENT to reserve the spot. Custom message replaces the default
          wording on the page.
        </span>
      </div>
      {draft.kind === 'ticket' && bundleCandidates.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] uppercase tracking-[0.06em] text-mutedDim">Includes merch</span>
          {bundleCandidates.map((m) => (
            <Toggle
              key={m.id}
              on={bundleIds.includes(m.id)}
              label={m.name}
              onChange={(v) =>
                setBundleIds((prev) => (v ? [...prev, m.id] : prev.filter((id) => id !== m.id)))
              }
            />
          ))}
          <span className="text-[10.5px] text-mutedDim">shown as “Includes: …” on the page; bundled stock is counted down on each sale</span>
        </div>
      ) : null}
      <div
        className={`flex flex-wrap items-center gap-2 rounded-lg transition-shadow ${
          imgDragOver ? 'ring-1 ring-accent' : ''
        }`}
        onDragOver={(e) => {
          if (draft.kind === 'merch' && e.dataTransfer.types.includes('Files')) {
            e.preventDefault();
            setImgDragOver(true);
          }
        }}
        onDragLeave={() => setImgDragOver(false)}
        onDrop={(e) => {
          if (draft.kind !== 'merch') return;
          e.preventDefault();
          setImgDragOver(false);
          const f = Array.from(e.dataTransfer.files).find((x) => x.type.startsWith('image/'));
          if (f) void uploadItemImage(f);
        }}
      >
        {/* Tickets render as stubs (no image on the page), so the uploader is merch-only. */}
        {draft.kind === 'merch' && draft.image_url ? (
          <img src={draft.image_url} alt="" className="h-10 w-10 rounded-md object-cover" />
        ) : null}
        {draft.kind === 'merch' ? (
          <button
            type="button"
            className="admin-btn admin-btn-secondary admin-btn-sm"
            onClick={() => fileRef.current?.click()}
            title="Or drag an image onto this row"
          >
            {draft.image_url ? 'Replace image' : 'Add image'}
          </button>
        ) : null}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            await uploadItemImage(f);
            e.target.value = '';
          }}
        />
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] tabular-nums text-mutedDim">{draft.sold} sold</span>
          <button type="button" className="admin-btn admin-btn-primary admin-btn-sm" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save item'}
          </button>
          <button
            type="button"
            className="rounded-lg border border-red-500/40 px-3 py-1.5 text-[11.5px] text-red-300"
            onClick={async () => {
              if (!window.confirm(`Delete "${draft.name}"?`)) return;
              const { error } = await supabase.from('shado_event_items').delete().eq('id', item.id);
              if (error) setErr(error.message);
              else onDeleted(item.id);
            }}
          >
            Delete
          </button>
        </div>
      </div>
      {err ? <div className="text-[11.5px] text-red-300">{err}</div> : null}
    </div>
  );
}

export function EventsScreen() {
  const [events, setEvents] = useState<EventRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<EventRow | null>(null);
  const [items, setItems] = useState<ItemRow[]>([]);

  /**
   * The gate the popup will quote: MIN across this event's FREE gated tickets, i.e. the lowest bar
   * the event offers. Mirrors `get_event_promo_popup()` exactly — `price_cents === 0` is
   * `event_rsvp`'s own condition, because a paid ticket's `min_logs` is a discount rule, not a door.
   *
   * ⚠️ `Number.isFinite`, NOT `|| 0`. `Math.min` over an empty list returns `Infinity`, and
   * `Infinity || 0` is `Infinity` — an event with no free ticket would have claimed a requirement of
   * Infinity and the tab would have drawn a gate nobody can clear.
   */
  const minLogsForPopup = useMemo(() => {
    const gates = items
      .filter(
        (i) => i.active && i.kind === 'ticket' && i.price_cents === 0 && (i.entitlement?.min_logs ?? 0) > 0,
      )
      .map((i) => i.entitlement?.min_logs ?? 0);
    const lo = Math.min(...gates);
    return Number.isFinite(lo) ? lo : 0;
  }, [items]);
  const [rsvps, setRsvps] = useState<RsvpRow[] | null>(null);
  const [rsvpSort, setRsvpSort] = useState<{
    key: 'name' | 'phone' | 'friends' | 'ratings' | 'joined' | 'when';
    dir: 1 | -1;
  }>({ key: 'when', dir: -1 });
  const [pendingClaims, setPendingClaims] = useState<RsvpRow[]>([]);
  const [rsvpQuery, setRsvpQuery] = useState(''); // door search (events_v22)
  const [editorTab, setEditorTab] = useState<
    'general' | 'items' | 'attendees' | 'popup' | 'texts' | 'push' | 'graphics'
  >('general');
  const [orders, setOrders] = useState<OrderRow[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const flyerRef = useRef<HTMLInputElement | null>(null);
  /** Drag-and-drop onto the flyer row = same as picking the file (Evan 2026-10-28). */
  const [flyerDragOver, setFlyerDragOver] = useState(false);
  const [heroPrompt, setHeroPrompt] = useState(() => loadHeroGen().prompt ?? '');
  const [heroRefs, setHeroRefs] = useState<RefImage[]>(() => loadHeroGen().refs ?? []);
  /** Past generations (storage URLs) — click one to put it back in the flyer slot. */
  const [heroHistory, setHeroHistory] = useState<string[]>(() => loadHeroGen().history ?? []);
  const [heroBusy, setHeroBusy] = useState(false);
  /** Generator status shown INLINE next to the AI-generate button — the shared `notice` renders
   * down by Save, so a failure there looked like "nothing happened" (Evan 2026-10-28). */
  const [heroNotice, setHeroNotice] = useState<string | null>(null);
  useEffect(() => {
    saveHeroGen({ prompt: heroPrompt, refs: heroRefs, history: heroHistory });
  }, [heroPrompt, heroRefs, heroHistory]);
  // Lineup/requirements are RAW TEXT while editing and parsed only on save. Parsing on every
  // keystroke (the first version) meant `.trim()` ate the space the moment it was typed.
  const [lineupText, setLineupText] = useState('');
  const [scheduleRows, setScheduleRows] = useState<{ time: string; label: string }[]>([]);
  const [scheduleRight, setScheduleRight] = useState<string[]>([]);
  const [bulletRows, setBulletRows] = useState<string[]>([]);
  const [requirementsText, setRequirementsText] = useState('');
  const [newEventOpen, setNewEventOpen] = useState(false);
  const [newEventTitle, setNewEventTitle] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    const { data, error: e } = await supabase
      .from('shado_events')
      .select('*')
      .order('created_at', { ascending: false });
    if (e) {
      setError(
        /shado_events/.test(e.message)
          ? 'Needs SQL applied: shado-admin/supabase/sql/events_v1.sql'
          : e.message,
      );
      return;
    }
    setEvents((data ?? []) as EventRow[]);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * RSVPs + orders + pending claimers for one event. Split out of `select` so the Attendees tab
   * can refresh them in place (Evan 2026-11-03: "should be able to refresh attendees and orders on
   * that page without having to refresh my site"). Items are NOT reloaded here — the Tickets tab
   * may hold unsaved edits.
   */
  const loadLists = useCallback(async (eventId: string) => {
    const ev = { id: eventId };
    const [{ data: rs }, { data: ord }] = await Promise.all([
      supabase
        .from('shado_event_rsvps')
        .select('*')
        .eq('event_id', ev.id)
        .order('created_at', { ascending: false }),
      supabase
        .from('shado_event_orders')
        .select('*')
        .eq('event_id', ev.id)
        .order('created_at', { ascending: false }),
    ]);
    setOrders((ord ?? []) as OrderRow[]);
    const rows = (rs ?? []) as RsvpRow[];
    const userIds = Array.from(new Set(rows.map((r) => r.user_id).filter(Boolean))) as string[];
    if (userIds.length > 0) {
      const { data: profs } = await supabase.from('profiles').select('id, username').in('id', userIds);
      const byId = new Map((profs ?? []).map((p) => [p.id as string, p.username as string]));
      rows.forEach((r) => {
        r.username = r.user_id ? byId.get(r.user_id) ?? null : null;
      });
      // Per-guest stats (events_v17): mutual-follow count, live ratings, account age — the
      // list sorts on these (Evan 2026-10-28: "so i can sort personally by this as I see fit").
      const { data: stats } = await supabase.rpc('admin_event_rsvp_stats', { p_event_id: ev.id });
      const statById = new Map(
        ((stats ?? []) as {
          user_id: string; friends: number; ratings: number; joined: string;
          is_21?: boolean | null;
        }[]).map((s) => [s.user_id, s]),
      );
      rows.forEach((r) => {
        const s = r.user_id ? statById.get(r.user_id) : undefined;
        r.friends = s?.friends ?? null;
        r.ratings = s?.ratings ?? null;
        r.joined = s?.joined ?? null;
        r.is_21 = s?.is_21 ?? null;
      });
    }
    setRsvps(rows);
    // Pending claimers (events_v24): verified but short of the ratings requirement — they ride
    // the same list with a red count and claim automatically when they qualify.
    const { data: pend } = await supabase.rpc('admin_event_pending_claims', { p_event_id: ev.id });
    setPendingClaims(
      ((pend ?? []) as {
        id: string; name: string; username: string | null; phone: string | null;
        user_id: string; created_at: string; ratings: number; needed: number;
        friends: number; joined: string; is_21?: boolean | null;
      }[]).map((p) => ({
        id: p.id,
        name: p.name,
        phone: p.phone,
        user_id: p.user_id,
        created_at: p.created_at,
        username: p.username,
        friends: p.friends,
        ratings: p.ratings,
        joined: p.joined,
        pending: true,
        needed: p.needed,
        is_21: p.is_21 ?? null,
      })),
    );
  }, []);

  const [listsRefreshing, setListsRefreshing] = useState(false);
  const [listsAt, setListsAt] = useState<number | null>(null);
  const refreshLists = useCallback(async () => {
    if (!selectedId) return;
    setListsRefreshing(true);
    try {
      await loadLists(selectedId);
      setListsAt(Date.now());
    } finally {
      setListsRefreshing(false);
    }
  }, [selectedId, loadLists]);
  // While the Attendees tab is open and visible, pull new RSVPs/orders every 30s.
  useEffect(() => {
    if (editorTab !== 'attendees' || !selectedId) return;
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refreshLists();
    }, 30_000);
    return () => window.clearInterval(t);
  }, [editorTab, selectedId, refreshLists]);

  const select = useCallback(async (ev: EventRow) => {
    setSelectedId(ev.id);
    setDraft({ ...ev });
    setLineupText(ev.lineup.map((l) => (l.time ? `${l.act} — ${l.time}` : l.act)).join('\n'));
    setScheduleRows((ev.schedule ?? []).map((s) => ({ time: s.time ?? '', label: s.label })));
    setScheduleRight(ev.schedule_right ?? []);
    setBulletRows(ev.description_bullets ?? []);
    setRequirementsText(ev.requirements.join('\n'));
    setRsvps(null);
    setOrders(null);
    setPendingClaims([]);
    const { data: it } = await supabase.from('shado_event_items').select('*').eq('event_id', ev.id).order('sort');
    setItems((it ?? []) as ItemRow[]);
    await loadLists(ev.id);
  }, [loadLists]);


  const createEvent = useCallback(async () => {
    const title = newEventTitle.trim();
    if (!title || creating) return;
    setCreating(true);
    const slug = slugify(title);
    const { data, error: e } = await supabase
      .from('shado_events')
      .insert({ title, slug, published: false })
      .select('*')
      .single();
    setCreating(false);
    if (e) {
      setError(/duplicate key/.test(e.message) ? `An event already uses the link /${slug}` : e.message);
      return;
    }
    setNewEventOpen(false);
    setNewEventTitle('');
    const row = data as EventRow;
    setEvents((prev) => [row, ...(prev ?? [])]);
    void select(row);
  }, [newEventTitle, creating, select]);

  const saveEvent = useCallback(async () => {
    if (!draft) return;
    setSaving(true);
    setNotice(null);
    // theme carries keys this editor does not manage (when_art, og_image — set from chat/scripts).
    // A stale open tab once wiped them by writing its whole theme back (2026-10-28), so saves
    // re-read the live theme and lay only the editor's own four keys on top of it.
    const { data: liveRow } = await supabase
      .from('shado_events')
      .select('theme')
      .eq('id', draft.id)
      .maybeSingle();
    const mergedTheme = {
      ...((liveRow?.theme as Record<string, unknown> | null) ?? {}),
      accent: draft.theme?.accent ?? null,
      hero: draft.theme?.hero ?? 'takeover',
      presents_color: draft.theme?.presents_color ?? null,
      title_color: draft.theme?.title_color ?? null,
      og_title: draft.theme?.og_title?.trim() || null,
    };
    const { error: e } = await supabase
      .from('shado_events')
      .update({
        slug: slugify(draft.slug || draft.title),
        title: draft.title,
        tagline: draft.tagline,
        flyer_url: draft.flyer_url,
        starts_at: draft.starts_at,
        doors_at: draft.doors_at,
        ends_at: draft.ends_at,
        venue_name: draft.venue_name,
        address: draft.address,
        city: draft.city,
        show_lineup: draft.show_lineup,
        show_requirements: draft.show_requirements,
        show_guest_list: draft.show_guest_list,
        show_countdown: draft.show_countdown,
        rsvp_open: draft.rsvp_open,
        published: draft.published,
        theme: mergedTheme,
        // Written only once the column exists (events_v4_contact.sql) — select('*') rows carry
        // the key after that, so saves never 42703 on a pre-migration database.
        ...('contact' in draft ? { contact: draft.contact ?? null } : {}),
        // Same pattern (events_v14): saves skip the column until the migration lands.
        ...('date_line' in draft ? { date_line: draft.date_line?.trim() || null } : {}),
        ...('max_capacity' in draft ? { max_capacity: draft.max_capacity ?? null } : {}),
        // Same pattern (20261102000000): saves skip the column until the migration lands.
        ...('popup' in draft ? { popup: draft.popup ?? null } : {}),
        lineup: lineupText
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line) => {
            const m = line.split(/\s+—\s+|\s+-\s+/);
            return m.length > 1 ? { act: m[0], time: m.slice(1).join(' ') } : { act: line };
          }),
        // Timeline rows (time box + label box, Evan 2026-10-26). No rows = the page derives
        // Doors/Starts/Ends automatically.
        schedule: scheduleRows
          .map((r) => ({ time: r.time.trim(), label: r.label.trim() }))
          .filter((r) => r.time || r.label)
          .map((r) => ({ ...(r.time ? { time: r.time } : {}), label: r.label })),
        schedule_right: scheduleRight.map((s) => s.trim()).filter(Boolean),
        description: draft.description?.trim() || null,
        description_bullets: bulletRows.map((s) => s.trim()).filter(Boolean),
        requirements: requirementsText
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
        updated_at: new Date().toISOString(),
      })
      .eq('id', draft.id);
    setSaving(false);
    if (e) {
      setNotice(e.message);
      return;
    }
    setNotice('Saved.');
    setEvents((prev) => (prev ?? []).map((ev) => (ev.id === draft.id ? { ...draft } : ev)));
  }, [draft, lineupText, requirementsText, scheduleRows, scheduleRight, bulletRows]);

  const uploadFlyer = useCallback(
    async (f: File) => {
      if (!draft) return;
      try {
        const url = await uploadEventImage(draft.slug, f);
        setDraft((d) => (d ? { ...d, flyer_url: url } : d));
      } catch (ex) {
        setNotice(ex instanceof Error ? ex.message : 'upload failed');
      }
    },
    [draft],
  );

  /** AI hero via studio-image (Gemini): blank prompt = auto-brief from the event's own fields.
   * The result lands in the flyer slot as a preview; Save applies it like an upload would. */
  const generateHero = useCallback(async () => {
    if (!draft || heroBusy) return;
    setHeroBusy(true);
    setHeroNotice(null);
    const prompt =
      heroPrompt.trim() ||
      `Atmospheric nightlife event poster backdrop for "${draft.title}"` +
        `${draft.venue_name ? ` at ${draft.venue_name}` : ''}${draft.city ? ` in ${draft.city}` : ''}` +
        ' — moody, premium, dark, violet accent lighting, cinematic. No text, no words, no letters.';
    const { data, error: fnErr } = await supabase.functions.invoke('studio-image', {
      // mode 'image' = freeform — without it the fn frames everything as an abstract texture.
      body: {
        prompt,
        aspect: '9:16',
        mode: 'image',
        refs: heroRefs.map((r) => ({ mime: r.mime, data: r.data })),
      },
    });
    setHeroBusy(false);
    if (fnErr) {
      let msg = fnErr.message || 'Image generation failed';
      const ctx = (fnErr as { context?: Response }).context;
      if (ctx && typeof ctx.json === 'function') {
        try {
          const parsed = (await ctx.json()) as { error?: string };
          if (parsed?.error) msg = parsed.error;
        } catch {
          /* keep default */
        }
      }
      if (/prepayment credits|RESOURCE_EXHAUSTED|402/i.test(msg)) {
        msg = 'Gemini is out of prepaid credits — add credits at ai.studio/projects, then retry.';
      }
      setHeroNotice(msg);
      return;
    }
    const d = (data ?? {}) as { url?: string };
    if (!d.url) {
      setHeroNotice('Image generation returned nothing');
      return;
    }
    setDraft((dd) => (dd ? { ...dd, flyer_url: d.url ?? dd.flyer_url } : dd));
    setHeroHistory((h) => [...h.filter((u) => u !== d.url), d.url as string].slice(-8));
    setHeroNotice('Generated — it is in the flyer slot now; Save to publish it.');
  }, [draft, heroBusy, heroPrompt, heroRefs]);

  /** Guest management (Evan 2026-10-26: "i also need management capability to remove guests")
   * — admin_event_remove_rsvp, same is_admin gate as every admin RPC. */
  const removeRsvp = useCallback(async (r: RsvpRow) => {
    if (!window.confirm(`Remove ${r.name} from the guest list?`)) return;
    const { error } = await supabase.rpc('admin_event_remove_rsvp', { p_rsvp_id: r.id });
    if (error) {
      setNotice(error.message);
      return;
    }
    setRsvps((prev) => (prev ? prev.filter((x) => x.id !== r.id) : prev));
  }, []);

  /** Shipped toggle (events_v20) — optimistic flip, is_admin-gated RPC persists it. */
  const toggleShipped = useCallback(async (o: OrderRow) => {
    const next = o.shipped_at ? null : new Date().toISOString();
    setOrders((prev) => (prev ? prev.map((x) => (x.id === o.id ? { ...x, shipped_at: next } : x)) : prev));
    const { error } = await supabase.rpc('admin_order_set_shipped', {
      p_order_id: o.id,
      p_shipped: next !== null,
    });
    if (error) {
      setNotice(error.message);
      setOrders((prev) => (prev ? prev.map((x) => (x.id === o.id ? { ...x, shipped_at: o.shipped_at } : x)) : prev));
    }
  }, []);

  const addItem = useCallback(
    async (kind: 'ticket' | 'merch') => {
      if (!draft) return;
      const { data, error: e } = await supabase
        .from('shado_event_items')
        .insert({
          event_id: draft.id,
          kind,
          name: kind === 'ticket' ? 'General admission' : 'New merch item',
          sort: items.length,
        })
        .select('*')
        .single();
      if (e) {
        setNotice(e.message);
        return;
      }
      setItems((prev) => [...prev, data as ItemRow]);
    },
    [draft, items.length],
  );

  /** Merch shipping toggle (events_v21) in the order panel — persists immediately. */
  const toggleCollectShipping = useCallback(async (it: ItemRow) => {
    const next = !(it.collect_shipping !== false);
    setItems((prev) => prev.map((p) => (p.id === it.id ? { ...p, collect_shipping: next } : p)));
    const { error } = await supabase
      .from('shado_event_items')
      .update({ collect_shipping: next })
      .eq('id', it.id);
    if (error) setNotice(error.message);
  }, []);

  /** Door check-in (events_v22) — optimistic flip, is_admin-gated RPC persists it. */
  const toggleCheckIn = useCallback(async (r: RsvpRow) => {
    const next = r.checked_in_at ? null : new Date().toISOString();
    setRsvps((prev) => (prev ? prev.map((x) => (x.id === r.id ? { ...x, checked_in_at: next } : x)) : prev));
    const { error } = await supabase.rpc('admin_event_check_in', {
      p_rsvp_id: r.id,
      p_checked: next !== null,
    });
    if (error) {
      setNotice(error.message);
      setRsvps((prev) =>
        prev ? prev.map((x) => (x.id === r.id ? { ...x, checked_in_at: r.checked_in_at } : x)) : prev,
      );
    }
  }, []);

  /** Rename a guest / a buyer (events_v26) — prompt-driven, optimistic with rollback. */
  const renameRsvp = useCallback(async (r: RsvpRow) => {
    const next = window.prompt('Guest name', r.name)?.trim();
    if (!next || next.length < 2 || next === r.name) return;
    setRsvps((prev) => (prev ? prev.map((x) => (x.id === r.id ? { ...x, name: next } : x)) : prev));
    const { error } = await supabase.rpc('admin_event_rename_rsvp', { p_id: r.id, p_name: next });
    if (error) {
      setNotice(error.message);
      setRsvps((prev) => (prev ? prev.map((x) => (x.id === r.id ? { ...x, name: r.name } : x)) : prev));
    }
  }, []);
  const renameOrder = useCallback(async (o: OrderRow) => {
    const next = window.prompt('Buyer name', o.buyer_name ?? '')?.trim();
    if (!next || next.length < 2 || next === o.buyer_name) return;
    setOrders((prev) => (prev ? prev.map((x) => (x.id === o.id ? { ...x, buyer_name: next } : x)) : prev));
    const { error } = await supabase.rpc('admin_order_set_buyer_name', { p_id: o.id, p_name: next });
    if (error) {
      setNotice(error.message);
      setOrders((prev) =>
        prev ? prev.map((x) => (x.id === o.id ? { ...x, buyer_name: o.buyer_name } : x)) : prev,
      );
    }
  }, []);

  /** Order panel ↑/↓ — swaps the two rows, renumbers `sort` 0..n, persists immediately. */
  const reorderItem = useCallback(
    async (idx: number, dir: -1 | 1) => {
      const next = moveItem(items, idx, dir).map((it, i2) => ({ ...it, sort: i2 }));
      setItems(next);
      const results = await Promise.all(
        next.map((it) => supabase.from('shado_event_items').update({ sort: it.sort }).eq('id', it.id)),
      );
      const bad = results.find((r) => r.error);
      if (bad?.error) setNotice(bad.error.message);
    },
    [items],
  );

  // Live/draft is the one switch that must never hide behind Save: it persists IMMEDIATELY.
  const togglePublished = useCallback(async () => {
    if (!draft) return;
    const next = !draft.published;
    setDraft((d) => (d ? { ...d, published: next } : d));
    const { error: e } = await supabase
      .from('shado_events')
      .update({ published: next, updated_at: new Date().toISOString() })
      .eq('id', draft.id);
    if (e) {
      setDraft((d) => (d ? { ...d, published: !next } : d));
      setNotice(e.message);
      return;
    }
    setEvents((prev) => (prev ?? []).map((ev) => (ev.id === draft.id ? { ...ev, published: next } : ev)));
    setNotice(next ? 'Event is LIVE — the link works now.' : 'Unpublished — the link shows “No event here.”');
  }, [draft]);

  const publicUrl = draft ? `${PUBLIC_HOST}/${draft.slug}` : '';
  const paidRevenueCents = (orders ?? [])
    .filter((o) => o.status === 'paid')
    .reduce((sum, o) => sum + o.amount_cents, 0);
  const itemNameById = new Map(items.map((i) => [i.id, i.name]));

  return (
    <div className="admin-page mx-auto max-w-5xl">
      <PageHeader
        title="Events"
        subtitle="Shado's own Partiful — event pages with RSVP, lineup, requirements, tickets and merch."
        actions={
          <button type="button" className="admin-btn admin-btn-primary" onClick={() => setNewEventOpen(true)}>
            New event
          </button>
        }
      />
      {error ? <div className="admin-banner-error mb-4">{error}</div> : null}
      {events === null && !error ? <SkeletonRows rows={6} /> : null}

      {events !== null && events.length === 0 ? (
        <EmptyState title="No events yet" hint="Create the first one — the public page goes live the moment you flip Published." />
      ) : null}

      {events !== null && events.length > 0 ? (
        <div className="mb-6 flex flex-wrap gap-3">
          {events.map((ev) => {
            const accent = ev.theme?.accent || '#8B5CF6';
            const selected = selectedId === ev.id;
            const when = ev.starts_at
              ? new Date(ev.starts_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
              : null;
            return (
              <button
                key={ev.id}
                type="button"
                onClick={() => void select(ev)}
                className={`group relative h-40 w-40 overflow-hidden rounded-2xl border text-left transition-all duration-150 ${
                  selected ? 'scale-[1.02]' : 'border-border hover:scale-[1.02]'
                }`}
                style={{
                  borderColor: selected ? accent : undefined,
                  boxShadow: selected ? `0 0 0 1px ${accent}, 0 8px 28px ${accent}44` : undefined,
                  background: ev.flyer_url
                    ? undefined
                    : `linear-gradient(160deg, ${accent}2e 0%, #15151a 55%, #0e0e12 100%)`,
                }}
              >
                {ev.flyer_url ? (
                  <>
                    <img
                      src={ev.flyer_url}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/15" />
                  </>
                ) : null}
                <div className="relative flex h-full flex-col justify-between p-3">
                  <div className="flex items-center gap-1.5">
                    <span
                      className="rounded-full px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.12em]"
                      style={
                        ev.published
                          ? { background: `${accent}33`, color: '#fff', boxShadow: `inset 0 0 0 1px ${accent}88` }
                          : { background: 'rgba(255,255,255,0.08)', color: '#9a9aa3', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.14)' }
                      }
                    >
                      {ev.published ? 'Live' : 'Draft'}
                    </span>
                    {when ? <span className="text-[10.5px] font-semibold text-white/80">{when}</span> : null}
                  </div>
                  <div>
                    <div className="text-[15px] font-bold leading-tight text-white" style={{ textShadow: '0 1px 8px rgba(0,0,0,0.6)' }}>
                      {ev.title}
                    </div>
                    <div className="mt-0.5 text-[10.5px] text-white/60">/{ev.slug}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : null}

      {draft ? (
        <div className="space-y-6">
          {/* Editor tabs (Evan 2026-10-28: "need a few key tabs for navigation of my event page") */}
          <div className="flex gap-2">
            {(
              [
                ['general', 'General'],
                ['items', 'Tickets & merch'],
                ['attendees', 'Attendees & orders'],
                ['popup', 'App popup'],
                ['texts', 'Texts'],
                ['push', 'App notifications'],
                ['graphics', 'QR & graphics'],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setEditorTab(k)}
                className={`rounded-full border px-4 py-1.5 text-[12.5px] font-medium ${
                  editorTab === k
                    ? 'border-accent bg-accent/15 text-accent'
                    : 'border-border text-mutedDim hover:text-white'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {editorTab === 'general' ? (
          <div className="admin-card space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <div className="admin-eyebrow">Event</div>
                <button
                  type="button"
                  onClick={() => void togglePublished()}
                  title={draft.published ? 'Tap to unpublish' : 'Tap to go live'}
                  className={`rounded-full border px-4 py-1.5 text-[12px] font-semibold tracking-wide transition-colors ${
                    draft.published
                      ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-300'
                      : 'border-amber-500/50 bg-amber-500/10 text-amber-300'
                  }`}
                >
                  {draft.published ? '● LIVE' : '○ DRAFT — tap to publish'}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[12px] text-accent underline-offset-2 hover:underline"
                >
                  {publicUrl.replace('https://', '')}
                </a>
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary admin-btn-sm"
                  onClick={() => void navigator.clipboard.writeText(publicUrl)}
                >
                  Copy link
                </button>
                {/* Separate public test site for the on-page checkout (Evan 2026-11-04): runs Stripe
                    in test mode, so nothing charges a real card. Same database as live. */}
                <a
                  href={`https://shado-events-test.vercel.app/${encodeURIComponent(draft.slug)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="admin-btn admin-btn-secondary admin-btn-sm"
                >
                  Open test site ↗
                </a>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <label className={labelCls}>Title</label>
                <input className={inputCls} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Slug (the link)</label>
                <input className={inputCls} value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Link preview text (shows under the image when the link is texted)</label>
                <input
                  className={inputCls}
                  maxLength={80}
                  value={draft.theme?.og_title ?? ''}
                  placeholder={`RSVP to ${draft.title || 'your event'}`}
                  onChange={(e) =>
                    setDraft({ ...draft, theme: { ...(draft.theme ?? {}), og_title: e.target.value } })
                  }
                />
                <div className="mt-1 text-[11px] text-mutedDim">
                  Leave blank for &ldquo;RSVP to {draft.title || 'your event'}&rdquo;. Apps cache previews, so a
                  link already sent keeps its old text.
                </div>
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Tagline</label>
                <input className={inputCls} value={draft.tagline ?? ''} onChange={(e) => setDraft({ ...draft, tagline: e.target.value || null })} />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Description — shows above the when/where card</label>
                <textarea
                  className={`${inputCls} min-h-[72px]`}
                  value={draft.description ?? ''}
                  placeholder="What this night is, in a sentence or two"
                  onChange={(e) => setDraft({ ...draft, description: e.target.value || null })}
                />
                <div className="mt-2 space-y-1.5">
                  {bulletRows.map((b, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-accent">•</span>
                      <input
                        className={`${inputCls} flex-1`}
                        value={b}
                        placeholder="Open bar 7–8"
                        onChange={(e) =>
                          setBulletRows((rows) => rows.map((x, j) => (j === i ? e.target.value : x)))
                        }
                      />
                      <button type="button" className="text-[12px] text-mutedDim hover:text-white" title="Move up" onClick={() => setBulletRows((rows) => moveItem(rows, i, -1))}>↑</button>
                      <button type="button" className="text-[12px] text-mutedDim hover:text-white" title="Move down" onClick={() => setBulletRows((rows) => moveItem(rows, i, 1))}>↓</button>
                      <button
                        type="button"
                        className="text-[13px] text-mutedDim hover:text-red-300"
                        title="Remove bullet"
                        onClick={() => setBulletRows((rows) => rows.filter((_, j) => j !== i))}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="admin-btn admin-btn-secondary admin-btn-sm"
                    onClick={() => setBulletRows((rows) => [...rows, ''])}
                  >
                    + Add bullet
                  </button>
                </div>
              </div>
              {/* Starts/Doors inputs removed (Evan 2026-10-29) — the hero shows the custom
                  date line only; starts_at keeps its stored value (feeds og previews + the
                  21+ event-date check), it just isn't edited here any more. */}
              <div>
                <label className={labelCls}>Date line — custom text on the hero</label>
                <input
                  className={inputCls}
                  placeholder="blank = automatic from Starts (e.g. Wednesday, November 5)"
                  value={draft.date_line ?? ''}
                  onChange={(e) => setDraft({ ...draft, date_line: e.target.value || null })}
                />
              </div>
              <div>
                <label className={labelCls}>Venue</label>
                <input className={inputCls} value={draft.venue_name ?? ''} onChange={(e) => setDraft({ ...draft, venue_name: e.target.value || null })} />
              </div>
              <div>
                <label className={labelCls}>Address</label>
                <input className={inputCls} value={draft.address ?? ''} onChange={(e) => setDraft({ ...draft, address: e.target.value || null })} />
              </div>
              <div>
                <label className={labelCls}>City</label>
                <input className={inputCls} value={draft.city ?? ''} onChange={(e) => setDraft({ ...draft, city: e.target.value || null })} />
              </div>
              <div>
                <label className={labelCls}>Max capacity — blank = unlimited</label>
                <input
                  className={inputCls}
                  inputMode="numeric"
                  placeholder="e.g. 150"
                  value={draft.max_capacity ?? ''}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      max_capacity: e.target.value.trim() === '' ? null : Math.max(0, Math.trunc(Number(e.target.value) || 0)),
                    })
                  }
                />
              </div>
              <div>
                <label className={labelCls}>Contact — email / phone / link</label>
                <input
                  className={inputCls}
                  placeholder="who guests reach with questions"
                  value={draft.contact ?? ''}
                  onChange={(e) => setDraft({ ...draft, contact: e.target.value || null })}
                />
                <p className="mt-1 text-[10.5px] leading-snug text-mutedDim">
                  Shows as a Contact button at the bottom of the page. Empty = no button.
                </p>
              </div>
              <div>
                <label className={labelCls}>Flyer — page hero + share-card image</label>
                <div
                  className={`flex items-center gap-2 rounded-lg transition-shadow ${
                    flyerDragOver ? 'ring-1 ring-accent' : ''
                  }`}
                  onDragOver={(e) => {
                    if (e.dataTransfer.types.includes('Files')) {
                      e.preventDefault();
                      setFlyerDragOver(true);
                    }
                  }}
                  onDragLeave={() => setFlyerDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setFlyerDragOver(false);
                    const f = Array.from(e.dataTransfer.files).find((x) => x.type.startsWith('image/'));
                    if (f) void uploadFlyer(f);
                  }}
                >
                  {draft.flyer_url ? <img src={draft.flyer_url} alt="" className="h-10 w-8 rounded object-cover" /> : null}
                  <button
                    type="button"
                    className="admin-btn admin-btn-secondary admin-btn-sm"
                    onClick={() => flyerRef.current?.click()}
                    title="Or drag an image onto this row"
                  >
                    {draft.flyer_url ? 'Replace flyer' : 'Upload flyer'}
                  </button>
                  <input
                    ref={flyerRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      await uploadFlyer(f);
                      e.target.value = '';
                    }}
                  />
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    className={inputCls}
                    placeholder="…or describe a hero for AI to make (blank = auto from the event)"
                    value={heroPrompt}
                    onChange={(e) => setHeroPrompt(e.target.value)}
                  />
                  <button
                    type="button"
                    className="admin-btn admin-btn-secondary admin-btn-sm whitespace-nowrap"
                    disabled={heroBusy}
                    onClick={() => void generateHero()}
                  >
                    {heroBusy ? 'Generating…' : 'AI generate'}
                  </button>
                </div>
                <div className="mt-2">
                  <RefImagePicker refs={heroRefs} setRefs={setHeroRefs} disabled={heroBusy} />
                </div>
                {heroNotice ? (
                  <p
                    className={`mt-1.5 text-[11.5px] leading-snug ${
                      heroNotice.startsWith('Generated') ? 'text-emerald-400' : 'text-amber-400'
                    }`}
                  >
                    {heroNotice}
                  </p>
                ) : null}
                {heroHistory.length > 0 ? (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="text-[9.5px] uppercase tracking-[0.12em] text-mutedDim">History</span>
                    {heroHistory
                      .slice()
                      .reverse()
                      .map((u) => (
                        <span key={u} className="relative inline-block">
                          <button
                            type="button"
                            title="Use as flyer"
                            onClick={() => setDraft((d) => (d ? { ...d, flyer_url: u } : d))}
                            className={`block overflow-hidden rounded-md border transition-colors ${
                              draft.flyer_url === u ? 'border-accent' : 'border-border hover:border-accent/60'
                            }`}
                          >
                            <img src={u} alt="" className="h-12 w-9 object-cover" />
                          </button>
                          <button
                            type="button"
                            aria-label="Remove from history"
                            onClick={() => setHeroHistory((h) => h.filter((x) => x !== u))}
                            className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full border border-border bg-black text-[9px] leading-none text-white hover:bg-zinc-800"
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                  </div>
                ) : null}
                <p className="mt-1 text-[10.5px] leading-snug text-mutedDim">
                  This picture is BOTH the big hero at the top of the page and the preview image
                  when the link is texted or posted. No flyer = a plain text card. AI generate
                  uses the studio&rsquo;s Gemini image model (needs GEMINI_API_KEY) and drops the
                  result straight into this slot — Save to publish it.
                </p>
              </div>
            </div>

            <div>
              <label className={labelCls}>Visual</label>
              <div className="mb-2 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="w-32 text-[11px] text-mutedDim">Accent</span>
                  <Swatches
                    value={draft.theme?.accent}
                    fallback="#8B5CF6"
                    onPick={(hex) =>
                      setDraft({ ...draft, theme: { ...(draft.theme ?? {}), accent: hex === '#8B5CF6' ? null : hex } })
                    }
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="w-32 text-[11px] text-mutedDim">"SHADO PRESENTS"</span>
                  <Swatches
                    value={draft.theme?.presents_color}
                    fallback="#C4AEFF"
                    onPick={(hex) =>
                      setDraft({
                        ...draft,
                        theme: { ...(draft.theme ?? {}), presents_color: hex === '#C4AEFF' ? null : hex },
                      })
                    }
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="w-32 text-[11px] text-mutedDim">Title</span>
                  <Swatches
                    value={draft.theme?.title_color}
                    fallback="#FFFFFF"
                    onPick={(hex) =>
                      setDraft({
                        ...draft,
                        theme: { ...(draft.theme ?? {}), title_color: hex === '#FFFFFF' ? null : hex },
                      })
                    }
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] text-mutedDim">Hero</span>
                <Toggle
                  on={(draft.theme?.hero ?? 'takeover') === 'takeover'}
                  label="poster takeover"
                  onChange={() => setDraft({ ...draft, theme: { ...(draft.theme ?? {}), hero: 'takeover' } })}
                />
                <Toggle
                  on={draft.theme?.hero === 'framed'}
                  label="framed flyer"
                  onChange={() => setDraft({ ...draft, theme: { ...(draft.theme ?? {}), hero: 'framed' } })}
                />
                <span className="text-[10.5px] text-mutedDim">
                  takeover = flyer fills the top with the title over it; framed = flyer in a card.
                  Accent recolors every button/highlight on the page. Save to apply.
                </span>
              </div>
            </div>

            <div>
              <label className={labelCls}>Page blocks</label>
              <div className="flex flex-wrap gap-2">
                <Toggle on={draft.rsvp_open} label="RSVP open" onChange={(v) => setDraft({ ...draft, rsvp_open: v })} />
                <Toggle on={draft.show_guest_list} label="guest list visible" onChange={(v) => setDraft({ ...draft, show_guest_list: v })} />
                <Toggle on={draft.show_lineup} label="lineup" onChange={(v) => setDraft({ ...draft, show_lineup: v })} />
                <Toggle on={draft.show_requirements} label="requirements" onChange={(v) => setDraft({ ...draft, show_requirements: v })} />
                <Toggle on={draft.show_countdown} label="countdown" onChange={(v) => setDraft({ ...draft, show_countdown: v })} />
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <label className={labelCls}>Timeline — the when-card rows</label>
                <div className="space-y-1.5">
                  {scheduleRows.map((r, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        className={`${inputCls} !w-28`}
                        value={r.time}
                        placeholder="7:00 PM"
                        onChange={(e) =>
                          setScheduleRows((rows) => rows.map((x, j) => (j === i ? { ...x, time: e.target.value } : x)))
                        }
                      />
                      <input
                        className={`${inputCls} flex-1`}
                        value={r.label}
                        placeholder="Doors open"
                        onChange={(e) =>
                          setScheduleRows((rows) => rows.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))
                        }
                      />
                      <button type="button" className="text-[12px] text-mutedDim hover:text-white" title="Move up" onClick={() => setScheduleRows((rows) => moveItem(rows, i, -1))}>↑</button>
                      <button type="button" className="text-[12px] text-mutedDim hover:text-white" title="Move down" onClick={() => setScheduleRows((rows) => moveItem(rows, i, 1))}>↓</button>
                      <button
                        type="button"
                        className="text-[13px] text-mutedDim hover:text-red-300"
                        title="Remove row"
                        onClick={() => setScheduleRows((rows) => rows.filter((_, j) => j !== i))}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="admin-btn admin-btn-secondary admin-btn-sm"
                    onClick={() => setScheduleRows((rows) => [...rows, { time: '', label: '' }])}
                  >
                    + Add row
                  </button>
                </div>
                <p className="mt-1 text-[10.5px] leading-snug text-mutedDim">
                  No rows = the page shows Doors/Starts/Ends from the time fields automatically.
                </p>
                <label className={`${labelCls} mt-3`}>Right side of the box — bullets</label>
                <div className="space-y-1.5">
                  {scheduleRight.map((b, i) => (
                    <div key={i} className="flex items-center gap-2">
                      {/* Purple toggle — stored as a leading * on the bullet text */}
                      <button
                        type="button"
                        title={b.startsWith('*') ? 'Purple on the page — click for white' : 'White on the page — click for purple'}
                        className={`h-4 w-4 flex-none rounded-full border ${
                          b.startsWith('*') ? 'border-accent bg-accent' : 'border-border bg-transparent'
                        }`}
                        onClick={() =>
                          setScheduleRight((rows) =>
                            rows.map((x, j) => (j === i ? (x.startsWith('*') ? x.slice(1) : `*${x}`) : x)),
                          )
                        }
                      />
                      <input
                        className={`${inputCls} flex-1`}
                        value={b.startsWith('*') ? b.slice(1) : b}
                        placeholder="Free parking after 10"
                        onChange={(e) =>
                          setScheduleRight((rows) =>
                            rows.map((x, j) =>
                              j === i ? (x.startsWith('*') ? `*${e.target.value}` : e.target.value) : x,
                            ),
                          )
                        }
                      />
                      <button type="button" className="text-[12px] text-mutedDim hover:text-white" title="Move up" onClick={() => setScheduleRight((rows) => moveItem(rows, i, -1))}>↑</button>
                      <button type="button" className="text-[12px] text-mutedDim hover:text-white" title="Move down" onClick={() => setScheduleRight((rows) => moveItem(rows, i, 1))}>↓</button>
                      <button
                        type="button"
                        className="text-[13px] text-mutedDim hover:text-red-300"
                        title="Remove"
                        onClick={() => setScheduleRight((rows) => rows.filter((_, j) => j !== i))}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="admin-btn admin-btn-secondary admin-btn-sm"
                    onClick={() => setScheduleRight((rows) => [...rows, ''])}
                  >
                    + Add bullet
                  </button>
                </div>
              </div>
              <div>
                <label className={labelCls}>Lineup — one per line: act — time</label>
                <textarea
                  className={`${inputCls} min-h-[96px]`}
                  value={lineupText}
                  onChange={(e) => setLineupText(e.target.value)}
                  placeholder={'DJ SMOKE — 10:00\nRICKY B — 11:30'}
                />
              </div>
              <div>
                <label className={labelCls}>Requirements — one per line</label>
                <textarea
                  className={`${inputCls} min-h-[96px]`}
                  value={requirementsText}
                  onChange={(e) => setRequirementsText(e.target.value)}
                  placeholder={'21+\nRSVP required for entry'}
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button type="button" className="admin-btn admin-btn-primary" disabled={saving} onClick={() => void saveEvent()}>
                {saving ? 'Saving…' : 'Save event'}
              </button>
              {notice ? <span className="text-[12px] text-muted">{notice}</span> : null}
            </div>
          </div>

          ) : null}
          {editorTab === 'items' ? (
          <div className="admin-card space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="admin-eyebrow">Tickets & merch</div>
              <div className="flex gap-2">
                <button type="button" className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => void addItem('ticket')}>
                  + Ticket
                </button>
                <button type="button" className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => void addItem('merch')}>
                  + Merch
                </button>
              </div>
            </div>
            {items.length > 1 ? (
              /* Order panel (Evan 2026-10-28: "small panel here where i can change order of
                 tickets, merch") — ↑/↓ swap `sort`, persisted immediately; the public page
                 renders tickets and merch in this order. */
              <div className="space-y-1 rounded-xl border border-border bg-surface px-3 py-2">
                <div className="text-[10px] uppercase tracking-[0.12em] text-mutedDim">Order on the page</div>
                {items.map((it, i) => (
                  <div key={it.id} className="flex items-center gap-2 text-[12px]">
                    <span className="w-4 text-right tabular-nums text-mutedDim">{i + 1}.</span>
                    <span className="text-white">{it.name || '(unnamed)'}</span>
                    <span className="text-[10px] text-mutedDim">{it.kind}</span>
                    {it.kind === 'merch' ? (
                      <button
                        type="button"
                        onClick={() => void toggleCollectShipping(it)}
                        title="Checked: Stripe collects a US shipping address. Unchecked: checkout says 'Pick up your order at the event.'"
                        className={`rounded-md border px-2 py-0.5 text-[10px] ${
                          it.collect_shipping !== false
                            ? 'border-accent bg-accent/15 text-accent'
                            : 'border-border text-mutedDim hover:text-white'
                        }`}
                      >
                        {it.collect_shipping !== false ? '☑ collect shipping address' : '☐ pickup at event'}
                      </button>
                    ) : null}
                    <span className="ml-auto flex gap-1.5">
                      <button
                        type="button"
                        title="Move up"
                        disabled={i === 0}
                        className="text-[12px] text-mutedDim hover:text-white disabled:opacity-30"
                        onClick={() => void reorderItem(i, -1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        title="Move down"
                        disabled={i === items.length - 1}
                        className="text-[12px] text-mutedDim hover:text-white disabled:opacity-30"
                        onClick={() => void reorderItem(i, 1)}
                      >
                        ↓
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
            {items.length === 0 ? (
              <p className="text-[12px] text-mutedDim">
                Nothing yet. Priced items get a real Stripe checkout button automatically; $0 items
                show “reserve with your RSVP”. A Shado perk (min logs + % off) makes an item cheap
                or free for active users, and a ticket can bundle merch.
              </p>
            ) : (
              <div className="space-y-2">
                {items.map((it) => (
                  <ItemEditor
                    key={it.id}
                    item={it}
                    slug={draft.slug}
                    siblings={items}
                    onSaved={(row) => setItems((prev) => prev.map((p) => (p.id === row.id ? row : p)))}
                    onDeleted={(id) => setItems((prev) => prev.filter((p) => p.id !== id))}
                  />
                ))}
              </div>
            )}
          </div>

          ) : null}
          {editorTab === 'attendees' ? (
          <>
          <div className="mb-3 flex items-center justify-end gap-3">
            <span className="text-[11px] text-mutedDim">
              {listsAt ? `Updated ${timeAgo(new Date(listsAt).toISOString())}` : 'Auto-refreshes every 30s'}
            </span>
            <button
              type="button"
              className="admin-btn admin-btn-secondary admin-btn-sm"
              disabled={listsRefreshing}
              onClick={() => void refreshLists()}
            >
              {listsRefreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
          <div className="admin-card space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="admin-eyebrow">
                Orders{orders ? ` — ${orders.length}` : ''}
                {orders && paidRevenueCents > 0 ? (
                  <span className="ml-2 normal-case tracking-normal text-emerald-300">
                    ${(paidRevenueCents / 100).toFixed(2)} collected
                  </span>
                ) : null}
              </div>
              {orders && orders.length > 0 ? (
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary admin-btn-sm"
                  onClick={() => {
                    const csv = [
                      'when,status,item,qty,size,amount,buyer,email,phone,ship_to',
                      ...orders.map((o) =>
                        [
                          o.created_at,
                          o.status,
                          o.item_id ? itemNameById.get(o.item_id) ?? '' : '',
                          o.qty,
                          o.size ?? '',
                          (o.amount_cents / 100).toFixed(2),
                          o.buyer_name ?? '',
                          o.buyer_email ?? '',
                          o.buyer_phone ?? '',
                          shippingLine(o.shipping) ?? '',
                        ]
                          .map((c) => `"${String(c).replace(/"/g, '""')}"`)
                          .join(','),
                      ),
                    ].join('\n');
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
                    a.download = `${draft.slug}-orders.csv`;
                    a.click();
                  }}
                >
                  Export CSV
                </button>
              ) : null}
            </div>
            {orders === null ? (
              <SkeletonRows rows={2} />
            ) : orders.length === 0 ? (
              <p className="text-[12px] text-mutedDim">
                No orders yet — every Stripe payment and every perk-comped item lands here the
                moment it happens.
              </p>
            ) : (
              <div className="space-y-1">
                {orders.map((o) => (
                  <div key={o.id} className="flex flex-wrap items-baseline gap-x-3 text-[12.5px]">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                        o.status === 'paid'
                          ? 'bg-emerald-500/15 text-emerald-300'
                          : 'bg-accent/15 text-accent'
                      }`}
                    >
                      {o.status}
                    </span>
                    <button
                      type="button"
                      onClick={() => void renameOrder(o)}
                      title="Edit name"
                      className="text-white hover:text-accent"
                    >
                      {o.buyer_name || o.buyer_email || o.buyer_phone || 'someone'}
                    </button>
                    {/* Email rides along when it isn't already the display name (Evan 2026-10-29). */}
                    {o.buyer_email && o.buyer_name ? (
                      <span className="text-[11px] text-mutedDim">{o.buyer_email}</span>
                    ) : null}
                    <span className="text-muted">
                      {o.item_id ? itemNameById.get(o.item_id) ?? 'item' : 'item'}
                      {o.size ? ` (${o.size})` : ''}
                      {o.qty > 1 ? ` ×${o.qty}` : ''}
                    </span>
                    <span className="tabular-nums text-white">${(o.amount_cents / 100).toFixed(2)}</span>
                    {shippingLine(o.shipping) ? (
                      <span className="text-[11px] text-mutedDim">→ {shippingLine(o.shipping)}</span>
                    ) : null}
                    <span className="ml-auto text-[11px] text-mutedDim">{timeAgo(o.created_at)}</span>
                    <button
                      type="button"
                      onClick={() => void toggleShipped(o)}
                      title={o.shipped_at ? 'Click to un-mark' : 'Mark as shipped'}
                      className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                        o.shipped_at
                          ? 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300'
                          : 'border-border text-mutedDim hover:text-white'
                      }`}
                    >
                      {o.shipped_at ? `Shipped ✓ ${timeAgo(o.shipped_at)}` : 'Mark shipped'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="admin-card space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="admin-eyebrow">
                RSVPs{rsvps ? ` — ${rsvps.length}` : ''}
                {rsvps && rsvps.some((r) => r.checked_in_at) ? (
                  <span className="ml-2 text-emerald-300">
                    {rsvps.filter((r) => r.checked_in_at).length} in
                  </span>
                ) : null}
              </div>
              {rsvps && rsvps.length + pendingClaims.length > 0 ? (
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary admin-btn-sm"
                  onClick={() => {
                    const csv = [
                      'status,name,phone,username,when,friends,ratings,joined_shado,is_21',
                      ...[...rsvps, ...pendingClaims].map((r) =>
                        [
                          r.pending ? `pending (${r.ratings ?? 0}/${r.needed ?? '?'})` : 'on list',
                          r.name,
                          r.phone ?? '',
                          r.username ?? '',
                          r.created_at,
                          r.friends ?? '',
                          r.ratings ?? '',
                          r.joined ?? '',
                          r.is_21 === true ? 'yes' : r.is_21 === false ? 'NO' : 'unknown',
                        ]
                          .map((c) => `"${String(c).replace(/"/g, '""')}"`)
                          .join(','),
                      ),
                    ].join('\n');
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
                    a.download = `${draft.slug}-rsvps.csv`;
                    a.click();
                  }}
                >
                  Export CSV
                </button>
              ) : null}
            </div>
            {rsvps && rsvps.length > 3 ? (
              <input
                className={`${inputCls} !h-8 !text-[12px]`}
                placeholder="Find a name at the door…"
                value={rsvpQuery}
                onChange={(e) => setRsvpQuery(e.target.value)}
              />
            ) : null}
            {rsvps === null ? (
              <SkeletonRows rows={3} />
            ) : rsvps.length + pendingClaims.length === 0 ? (
              <p className="text-[12px] text-mutedDim">Nobody yet — share the link.</p>
            ) : (
              /* Real columns, sortable by clicking a header (Evan 2026-10-28). Pending claimers
                 (verified, not enough ratings yet) ride along with a red count. */
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-mutedDim">
                    {(
                      [
                        ['name', 'Name'],
                        ['phone', 'Phone'],
                        ['friends', 'Friends'],
                        ['ratings', 'Ratings'],
                        ['joined', 'Joined'],
                        ['when', 'RSVPed'],
                      ] as const
                    ).map(([k, label]) => (
                      <th
                        key={k}
                        className="cursor-pointer select-none pb-1.5 pr-3 font-semibold hover:text-white"
                        onClick={() =>
                          setRsvpSort((s) =>
                            s.key === k ? { key: k, dir: (s.dir * -1) as 1 | -1 } : { key: k, dir: -1 },
                          )
                        }
                      >
                        {label}
                        {rsvpSort.key === k ? (rsvpSort.dir === -1 ? ' ↓' : ' ↑') : ''}
                      </th>
                    ))}
                    <th className="pb-1.5 pr-3 font-semibold" title="21 or older on the event date (from Shado birthdates)">
                      21+
                    </th>
                    <th className="pb-1.5" />
                    <th className="pb-1.5" />
                  </tr>
                </thead>
                <tbody>
                  {[...rsvps, ...pendingClaims]
                    .filter((r) => {
                      const q = rsvpQuery.trim().toLowerCase();
                      if (!q) return true;
                      return (
                        r.name.toLowerCase().includes(q) ||
                        (r.username ?? '').toLowerCase().includes(q) ||
                        (r.phone ?? '').includes(q.replace(/\D/g, '') || '\u0000')
                      );
                    })
                    .sort((a, b) => {
                      const d = rsvpSort.dir;
                      if (rsvpSort.key === 'name') return a.name.localeCompare(b.name) * -d;
                      if (rsvpSort.key === 'phone')
                        return ((a.phone ?? '') < (b.phone ?? '') ? 1 : -1) * d;
                      if (rsvpSort.key === 'friends') return ((a.friends ?? -1) - (b.friends ?? -1)) * d;
                      if (rsvpSort.key === 'ratings') return ((a.ratings ?? -1) - (b.ratings ?? -1)) * d;
                      if (rsvpSort.key === 'joined')
                        return (
                          (new Date(a.joined ?? '9999-01-01').getTime() -
                            new Date(b.joined ?? '9999-01-01').getTime()) * d
                        );
                      return (
                        (new Date(a.created_at).getTime() - new Date(b.created_at).getTime()) * d
                      );
                    })
                    .map((r) => (
                      <tr key={r.id} className="border-t border-border/60">
                        <td className="py-1.5 pr-3">
                          {/* Pending claimers aren't rsvp rows — their name isn't editable. */}
                          {r.pending ? (
                            <span className="text-white">{r.name}</span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => void renameRsvp(r)}
                              title="Edit name"
                              className="text-white hover:text-accent"
                            >
                              {r.name}
                            </button>
                          )}
                          {r.username ? <span className="ml-2 text-accent">@{r.username}</span> : null}
                        </td>
                        <td className="py-1.5 pr-3 tabular-nums text-mutedDim">{r.phone}</td>
                        <td className="py-1.5 pr-3 tabular-nums text-mutedDim">{r.friends ?? ''}</td>
                        <td
                          className={`py-1.5 pr-3 tabular-nums ${
                            r.pending ? 'font-semibold text-red-400' : 'text-mutedDim'
                          }`}
                          title={r.pending ? `Needs ${r.needed} rated venues to claim — on the list automatically when they get there` : undefined}
                        >
                          {r.pending ? `${r.ratings ?? 0}/${r.needed ?? '?'}` : r.ratings ?? ''}
                        </td>
                        <td className="py-1.5 pr-3 text-mutedDim">
                          {r.joined
                            ? new Date(r.joined).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                            : ''}
                        </td>
                        <td className="py-1.5 pr-3 text-[11px] text-mutedDim">{timeAgo(r.created_at)}</td>
                        {/* 21+ on the event date (events_v27): green ✓ / red ✗ / grey — (no birthdate). */}
                        <td
                          className={`py-1.5 pr-3 font-bold ${
                            r.is_21 === true ? 'text-emerald-300' : r.is_21 === false ? 'text-red-400' : 'text-mutedDim'
                          }`}
                          title={
                            r.is_21 === true
                              ? '21+ on the event date'
                              : r.is_21 === false
                                ? 'UNDER 21 on the event date — check ID'
                                : 'No birthdate on their Shado account — check ID'
                          }
                        >
                          {r.is_21 === true ? '✓' : r.is_21 === false ? '✗' : '—'}
                        </td>
                        <td className="py-1.5 pr-2 text-right">
                          {r.pending ? (
                            <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold text-amber-300">
                              pending
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => void toggleCheckIn(r)}
                              title={r.checked_in_at ? 'Click to undo' : 'Check in at the door'}
                              className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                                r.checked_in_at
                                  ? 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300'
                                  : 'border-border text-mutedDim hover:text-white'
                              }`}
                            >
                              {r.checked_in_at ? 'In ✓' : 'Check in'}
                            </button>
                          )}
                        </td>
                        <td className="py-1.5 text-right">
                          {r.pending ? null : (
                            <button
                              type="button"
                              className="text-[11px] font-medium text-red-400 hover:underline"
                              onClick={() => void removeRsvp(r)}
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
          </>
          ) : null}
          {editorTab === 'popup' ? (
            <EventPopupTab
              value={draft.popup ?? null}
              onChange={(next) => setDraft((d) => (d ? { ...d, popup: next } : d))}
              eventId={draft.id}
              eventSlug={draft.slug}
              dateLine={draft.date_line ?? null}
              venueName={draft.venue_name}
              eventTitle={draft.title}
              published={draft.published}
              /*
               * ⚠️ READ OFF THE ITEMS, NOT STORED ON THE POPUP. MIN across the event's FREE gated
               * tickets, which is exactly what `get_event_promo_popup()` does — the lowest bar the
               * event offers. `price_cents === 0` mirrors `event_rsvp`'s own condition: a paid
               * ticket's min_logs is a discount rule, not a door.
               */
              minLogs={minLogsForPopup}
            />
          ) : null}
          {editorTab === 'graphics' ? (
            <EventGraphicsTab
              slug={draft.slug}
              title={draft.title}
              dateLine={draft.date_line ?? null}
              flyerUrl={draft.flyer_url}
            />
          ) : null}
          {editorTab === 'push' ? (
            <EventPushTab
              slug={draft.slug}
              eventTitle={draft.title}
              guests={[
                ...(rsvps ?? []).map((r) => ({
                  name: r.name,
                  user_id: r.user_id,
                  group: (r.checked_in_at ? 'in' : 'list') as 'in' | 'list',
                })),
                ...pendingClaims.map((p) => ({ name: p.name, user_id: p.user_id, group: 'pending' as const })),
              ]}
            />
          ) : null}
          {editorTab === 'texts' ? (
            <EventTextsTab
              eventId={draft.id}
              guests={[
                ...(rsvps ?? []).map((r) => ({
                  name: r.name,
                  phone: r.phone,
                  tag: r.checked_in_at ? 'on list · in' : 'on list',
                })),
                ...(orders ?? [])
                  .filter((o) => o.status === 'paid' || o.status === 'comped')
                  .map((o) => ({ name: o.buyer_name || 'Buyer', phone: o.buyer_phone, tag: 'bought' })),
                ...pendingClaims.map((p) => ({ name: p.name, phone: p.phone, tag: 'pending' })),
              ]}
            />
          ) : null}
        </div>
      ) : null}

      {newEventOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setNewEventOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl border border-border bg-surface p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-eyebrow mb-1">New event</div>
            <p className="mb-3 text-[12px] text-mutedDim">
              Name it — the link is made from the title and both can be changed after.
            </p>
            <input
              className={inputCls}
              autoFocus
              placeholder="Event title"
              value={newEventTitle}
              onChange={(e) => setNewEventTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void createEvent();
                if (e.key === 'Escape') setNewEventOpen(false);
              }}
            />
            {newEventTitle.trim() ? (
              <div className="mt-1.5 text-[11px] text-mutedDim">
                Link: {PUBLIC_HOST.replace('https://', '')}/{slugify(newEventTitle)}
              </div>
            ) : null}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="admin-btn admin-btn-secondary"
                onClick={() => setNewEventOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-btn admin-btn-primary"
                disabled={!newEventTitle.trim() || creating}
                onClick={() => void createEvent()}
              >
                {creating ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
